// src/components/TransactionStatement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './TransactionStatement.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));


const TransactionStatement = ({ onWalletUpdate }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const { currentUser } = useAuth();

  // Filters
  const [filters, setFilters] = useState({
    period: 'monthly',
    startDate: '',
    endDate: '',
    transactionType: 'all',
    currency: 'all'
  });

  const [userSearchInput, setUserSearchInput] = useState('');

  // Period options
  const periodOptions = [
    { value: 'daily', label: 'Today' },
    { value: 'weekly', label: 'Last 7 Days' },
    { value: 'monthly', label: 'This Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const transactionTypes = [
    { value: 'all', label: 'All Transactions' },
    { value: 'admin_deposit', label: 'Admin Deposits' },
    { value: 'admin_withdrawal', label: 'Admin Withdrawals' },
    { value: 'deposit', label: 'User Deposits' },
    { value: 'withdrawal', label: 'User Withdrawals' },
    { value: 'game_entry', label: 'Game Entries' },
    { value: 'game_winning', label: 'Game Winnings' },
    { value: 'bonus', label: 'Bonus' },
    { value: 'cash_injection', label: 'Cash Injection' }
  ];

  const currencyOptions = [
    { value: 'all', label: 'All Currencies' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'LRD', label: 'LRD (L$)' }
  ];

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Search users
  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await axios.get(
        `${API_BASE}/api/admin/users/search`,
        {
          ...getAuthHeader(),
          params: { query }
        }
      );

      if (response.data.success) {
        setUserSearchResults(response.data.users || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format amount
  const formatAmount = (amount, currency) => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'LRD': 'L$'
    };
    const symbol = symbols[currency] || '';
    const numAmount = parseFloat(amount) || 0;
    return `${symbol}${Math.abs(numAmount).toFixed(2)}`;
  };

  // Get transaction type label
  const getTransactionTypeLabel = (type) => {
    const typeMap = {
      'admin_deposit': 'Admin Deposit',
      'admin_withdrawal': 'Admin Withdrawal',
      'deposit': 'User Deposit',
      'withdrawal': 'User Withdrawal',
      'game_entry': 'Game Entry',
      'game_winning': 'Game Winning',
      'bonus': 'Bonus',
      'cash_injection': 'Cash Injection'
    };
    return typeMap[type] || type.replace('_', ' ').toUpperCase();
  };

  // Fetch transactions
  const fetchTransactions = async (page = 1) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        limit: 10,
        period: filters.period,
        type: filters.transactionType !== 'all' ? filters.transactionType : undefined,
        currency: filters.currency !== 'all' ? filters.currency : undefined,
        startDate: filters.startDate,
        endDate: filters.endDate
      };

      // Add userId if selected
      if (selectedUser) {
        params.userId = selectedUser.id;
      }

      // Remove undefined parameters
      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const endpoint = selectedUser 
        ? `${API_BASE}/api/admin/users/transactions`
        : `${API_BASE}/api/admin/transactions/statement`;

      console.log('Fetching transactions from:', endpoint, 'with params:', params);

      const response = await axios.get(
        endpoint,
        { 
          ...getAuthHeader(),
          params 
        }
      );

      if (response.data.success) {
        setTransactions(response.data.transactions || []);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
        setTotalItems(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error.response?.data || error.message);
      alert('Failed to fetch transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setCurrentPage(1);
    fetchTransactions(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      period: 'monthly',
      startDate: '',
      endDate: '',
      transactionType: 'all',
      currency: 'all'
    });
    setSelectedUser(null);
    setUserSearchInput('');
    setCurrentPage(1);
    fetchTransactions(1);
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setUserSearchResults([]);
    setUserSearchInput(user.username);
  };

  // Clear selected user
  const clearSelectedUser = () => {
    setSelectedUser(null);
    setUserSearchInput('');
  };

  // Initialize
  useEffect(() => {
    fetchTransactions(1);
  }, []);

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchInput && userSearchInput.length >= 2 && !selectedUser) {
        searchUsers(userSearchInput);
      } else {
        setUserSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [userSearchInput, selectedUser]);

  // Handle pagination
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    fetchTransactions(page);
  };

  // Calculate totals
  const calculateTotals = () => {
    const credits = transactions
      .filter(tx => parseFloat(tx.amount) > 0)
      .reduce((sum, tx) => sum + parseFloat(tx.amount), 0);
    
    const debits = transactions
      .filter(tx => parseFloat(tx.amount) < 0)
      .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount)), 0);
    
    const net = credits - debits;
    
    return { credits, debits, net };
  };

  const totals = calculateTotals();

  return (
    <div className="transaction-statement">
      <div className="transaction-header">
        <h2>Transaction Statement</h2>
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-label">Total Transactions</div>
            <div className="summary-value">{totalItems}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Current Page</div>
            <div className="summary-value">{currentPage} / {totalPages}</div>
          </div>
          {selectedUser && (
            <div className="summary-card user-card">
              <div className="summary-label">Selected User</div>
              <div className="summary-value">{selectedUser.username}</div>
            </div>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Period</label>
            <select 
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
            >
              {periodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Transaction Type</label>
            <select 
              value={filters.transactionType}
              onChange={(e) => handleFilterChange('transactionType', e.target.value)}
            >
              {transactionTypes.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Currency</label>
            <select 
              value={filters.currency}
              onChange={(e) => handleFilterChange('currency', e.target.value)}
            >
              {currencyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Filter by User</label>
            <div className="user-search-container">
              <input 
                type="text"
                placeholder="Search username..."
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                disabled={!!selectedUser}
              />
              {selectedUser && (
                <button 
                  className="clear-user-btn"
                  onClick={clearSelectedUser}
                  title="Clear selected user"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              {userSearchLoading && (
                <div className="search-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
              )}
            </div>
            
            {userSearchResults.length > 0 && !selectedUser && (
              <div className="user-search-dropdown">
                {userSearchResults.map(user => (
                  <div 
                    key={user.id}
                    className="user-search-result"
                    onClick={() => handleUserSelect(user)}
                  >
                    <div className="user-info">
                      <strong>{user.username}</strong>
                      <small>{user.email}</small>
                    </div>
                    <div className="user-balance">
                      ₹{parseFloat(user.wallet_balance_inr || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {filters.period === 'custom' && (
          <div className="filter-row">
            <div className="filter-group">
              <label>Start Date</label>
              <input 
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input 
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="filter-actions">
          <button className="btn-apply" onClick={applyFilters}>
            <i className="fas fa-filter"></i> Apply Filters
          </button>
          <button className="btn-reset" onClick={resetFilters}>
            <i className="fas fa-redo"></i> Reset
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-table-container">
        {loading ? (
          <div className="loading-transactions">
            <div className="spinner"></div>
            <p>Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="no-transactions">
            <i className="fas fa-file-invoice"></i>
            <p>No transactions found for the selected filters</p>
          </div>
        ) : (
          <>
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Transaction ID</th>
                  <th>Type</th>
                  <th>Description</th>
                  {selectedUser ? (
                    <>
                      <th>Opening Balance</th>
                      <th>Credit</th>
                      <th>Debit</th>
                      <th>Closing Balance</th>
                    </>
                  ) : (
                    <>
                      <th>Amount</th>
                      <th>Currency</th>
                    </>
                  )}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className={`status-${transaction.status}`}>
                    <td>{formatDate(transaction.created_at)}</td>
                    <td className="txn-id">{transaction.reference_number}</td>
                    <td>
                      <span className={`txn-type type-${transaction.type}`}>
                        {getTransactionTypeLabel(transaction.type)}
                      </span>
                    </td>
                    <td className="txn-description">
                      {transaction.description}
                      {transaction.user_name && (
                        <div className="user-mention">
                          User: {transaction.user_name}
                        </div>
                      )}
                    </td>
                    
                    {selectedUser ? (
                      <>
                        <td>{formatAmount(transaction.opening_balance || 0, transaction.currency)}</td>
                        <td className="credit-amount">
                          {parseFloat(transaction.amount) > 0 ? 
                            formatAmount(transaction.amount, transaction.currency) : '-'}
                        </td>
                        <td className="debit-amount">
                          {parseFloat(transaction.amount) < 0 ? 
                            formatAmount(transaction.amount, transaction.currency) : '-'}
                        </td>
                        <td>{formatAmount(transaction.closing_balance || 0, transaction.currency)}</td>
                      </>
                    ) : (
                      <>
                        <td className={`txn-amount ${parseFloat(transaction.amount) >= 0 ? 'credit' : 'debit'}`}>
                          {formatAmount(transaction.amount, transaction.currency)}
                        </td>
                        <td>{transaction.currency}</td>
                      </>
                    )}
                    <td>
                      <span className={`status-badge status-${transaction.status}`}>
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  <i className="fas fa-chevron-left"></i> Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button 
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary Footer */}
      {transactions.length > 0 && (
        <div className="summary-footer">
          <div className="summary-item">
            <span>Total Credits:</span>
            <span className="credit-amount">
              {formatAmount(totals.credits, filters.currency !== 'all' ? filters.currency : 'INR')}
            </span>
          </div>
          <div className="summary-item">
            <span>Total Debits:</span>
            <span className="debit-amount">
              {formatAmount(totals.debits, filters.currency !== 'all' ? filters.currency : 'INR')}
            </span>
          </div>
          <div className="summary-item">
            <span>Net Balance:</span>
            <span className={`net-amount ${totals.net >= 0 ? 'positive' : 'negative'}`}>
              {formatAmount(totals.net, filters.currency !== 'all' ? filters.currency : 'INR')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionStatement;